import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FormApi, SubmissionErrors } from "final-form";
import { noop } from "lodash";
import { PureComponent, ReactNode, createRef } from "react";

import { ModalPortal as DSPortal, Text } from "@livechat/design-system-react-components";

import { DeleteTextButton } from "components/button/DeleteTextButton";
import { ModalPortal } from "components/modal";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import FormMode from "constants/form-mode";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { setDocumentTitle } from "helpers/routing";
import { willUpgradeSubscription } from "helpers/subscription";
import { onSubscriptionUpgradeFailed } from "hooks/use-payment-failure";
import { IStoreState } from "interfaces/storeState";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import AttachmentProvider from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { actions$ } from "store/action-stream";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { AGENT } from "store/entities/agents";
import { IAgent } from "store/entities/agents/interfaces";
import { IBilling } from "store/entities/billing";
import { IIntegration } from "store/entities/integrations";
import { selectManagedByPartner, selectManagedByPartnerEmail } from "store/entities/license";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import { ISubscription } from "store/entities/subscriptions";
import { IAction } from "store/helper";
import { getIsSavingAgent } from "store/views/agents";
import { StackingContextLevel } from "styles/stacking-context-level";
import { scrollToSection } from "utils/scroll";

import { RoleChangePermissions } from "../constants";

import { Agent2FA } from "./Agent2FA";
import AgentFormBase, { ICommonAgentFormProps } from "./AgentFormBase";
import { AgentFormSection } from "./AgentFormSection";
import AgentSignature from "./AgentSignature";
import { AutoAssignmentManagement } from "./AutoAssignmentManagement";
import NotificationsManagement from "./NotificationsManagement";
import PermissionManagement from "./PermissionManagement";
import { SubscriptionUpgradeModal } from "./SubscriptionUpgradeModal";
import { TeamMembershipManagement } from "./TeamMembershipManagement";
import BrowserNotificationsManagement from "./browser-notifications/BrowserNotificationsManagement";
import { AgentFormSectionId } from "./constants";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars
const mapStateToProps = (state: IStoreState) => ({
  isManagedByPartner: selectManagedByPartner(state),
  partnersContactEmail: selectManagedByPartnerEmail(state),
  isSavingAgent: getIsSavingAgent(state),
});

type StateProps = ReturnType<typeof mapStateToProps>;

// @ts-expect-error TS(2430)
export interface IAgentFormProps extends ICommonAgentFormProps, StateProps {
  agent: IAgent;
  subscription: ISubscription;
  billing: IBilling;
  nonViewersCount: number;
  isSelfManagement: boolean;
  deleteAgentEnabled: boolean;
  roleChangePermissions: RoleChangePermissions;
  canChangeSignature: boolean;
  canAccessSettings: boolean;
  mailboxes: IMailbox[];
  shopifyIntegration: IIntegration | undefined;
  isManagedByPartner: boolean;
  partnersContactEmail: string | undefined;
  onDelete(agentId: string): void;
  onLogout(): void;
  onSubmit: (
    agent: IAgent,
    form: FormApi<IAgent, Partial<IAgent>>,
    callback: (errors?: SubmissionErrors) => void,
    transactionID: string | null
  ) => void;
  onGoToMailboxes: () => void;
  isSavingAgent: boolean;
  isShopifyTrial?: boolean;
}

interface IState {
  initialFormValues: IAgent;
  unsavedChanges: boolean;
  agentDeleteModalShown: boolean;
  agentDeleteWarningModalShown: boolean;
  agentsLimitReachedModalShown: boolean;
  transactionID: string | null;
  paymentFailed: boolean;
}

export class AgentForm extends PureComponent<IAgentFormProps, IState> {
  private unsubscribe: () => void;
  constructor(props: IAgentFormProps) {
    super(props);

    this.state = {
      agentDeleteModalShown: false,
      agentDeleteWarningModalShown: false,
      agentsLimitReachedModalShown: false,
      unsavedChanges: false,
      initialFormValues: props.agent,
      transactionID: null,
      paymentFailed: false,
    };

    this.unsubscribe = (): void => {};
  }

  updateUnsubscribe = noop;

  componentDidMount(): void {
    const { agent } = this.props;
    if (agent) {
      setDocumentTitle(`Edit agent: ${agent.name}`);
    } else {
      setDocumentTitle("New agent");
    }
    this.unsubscribe = onSubscriptionUpgradeFailed(() => {
      this.setState({
        paymentFailed: true,
      });
    });

    this.updateUnsubscribe = pipe(
      actions$,
      filter((action: IAction) => action.type === AGENT[CRUDAction.UPDATE][RequestAction.FAILURE]),
      subscribe(() => {
        this.setState({
          agentsLimitReachedModalShown: false,
        });
      })
    );

    const locationHash = window.location.hash;
    const scrollToSectionId = locationHash && locationHash.substring(1);
    const sectionToScrollExists = (Object.values(AgentFormSectionId) as Array<string>).includes(scrollToSectionId);

    if (sectionToScrollExists) scrollToSection(scrollToSectionId as AgentFormSectionId);

    trackEngagement("Agent details: opened", EventPlace.AgentDetails, {
      ...(sectionToScrollExists ? { scrollTo: scrollToSectionId } : {}),
    });
  }

  componentWillUnmount(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.updateUnsubscribe();
  }

  private handleLogOutAgentButtonClick = (): void => {
    this.props.onLogout();
  };

  private handleDeleteAgentButtonClick = (): void => {
    if (this.isAgentUsedInMailboxes()) {
      this.toggleAgentDeleteWarningModalShown();
    } else {
      this.toggleAgentDeleteModalShown();
    }
  };

  private handleAgentDeleteCancel = (): void => {
    this.toggleAgentDeleteModalShown();
  };

  private handleAgentDeleteConfirm = (): void => {
    this.toggleAgentDeleteModalShown();
    this.props.onDelete(this.props.agent.id);
  };

  private handleAgentDeleteWarningCancel = (): void => {
    this.toggleAgentDeleteWarningModalShown();
  };

  private handleSubmit = (
    agent: IAgent,
    form: FormApi<IAgent, Partial<IAgent>>,
    onSaving: () => void,
    onSuccess: (errors?: SubmissionErrors) => void
  ): void => {
    const {
      agent: unmodifiedAgent,
      subscription,
      billing,
      nonViewersCount,
      onSubmit,
      isManagedByPartner,
      shopifyIntegration,
    } = this.props;
    const { transactionID } = this.state;

    const isUpgradingViewer = unmodifiedAgent.permission === Role.Viewer && agent.permission !== Role.Viewer;
    const isUpgradeModalShown = this.state.agentsLimitReachedModalShown === true;
    const isShopifyTrial = this.props.isShopifyTrial || false;
    const isManaged = isManagedByPartner || !!shopifyIntegration;
    const willUpgrade = willUpgradeSubscription(subscription, billing, nonViewersCount, 1, isManaged);

    if (isUpgradingViewer && willUpgrade && !isUpgradeModalShown && !isShopifyTrial) {
      this.setState(
        {
          agentsLimitReachedModalShown: true,
        },
        () => {
          onSuccess();
        }
      );
    } else {
      onSaving();
      onSubmit(agent, form, onSuccess, transactionID);
    }
  };

  private readonly formBaseRef = createRef<AgentFormBase>();

  private toggleAgentDeleteModalShown = (): void => {
    this.setState(prevState => ({
      agentDeleteModalShown: !prevState.agentDeleteModalShown,
    }));
  };

  private toggleAgentDeleteWarningModalShown = (): void => {
    this.setState(prevState => ({
      agentDeleteWarningModalShown: !prevState.agentDeleteWarningModalShown,
    }));
  };

  private shouldShowDeletePanel(): boolean {
    return this.props.mode === FormMode.Edit && this.props.deleteAgentEnabled;
  }

  private handleLimitReachedBack = (): void => {
    this.setState({
      agentsLimitReachedModalShown: false,
    });
  };

  private handleLimitReachedConfirm = (): void => {
    this.formBaseRef.current?.form.submit();
  };

  private isAgentUsedInMailboxes = (): boolean =>
    !!this.props.mailboxes.find(mailbox => mailbox.agentId === this.props.agent.id);

  render(): ReactNode {
    const {
      agent,
      teams,
      isSelfManagement,
      canManageAgentTeams,
      canChangeSignature,
      canAccessSettings,
      roleChangePermissions,
      onGoToMailboxes,
      isSavingAgent,
    } = this.props;

    const {
      agentDeleteModalShown,
      agentDeleteWarningModalShown,
      agentsLimitReachedModalShown,
      initialFormValues,
      unsavedChanges,
      paymentFailed,
    } = this.state;

    return (
      <>
        <AttachmentProvider agentID={agent.id} onTransactionChange={transactionID => this.setState({ transactionID })}>
          <AgentFormBase
            {...this.props}
            initialFormValues={initialFormValues}
            loginFieldManagementEnabled
            unsavedChanges={unsavedChanges}
            onSubmit={this.handleSubmit}
            ref={this.formBaseRef}
            paymentFailed={paymentFailed}
            isSavingAgent={isSavingAgent}
          >
            {/* @ts-expect-error TS(2322): Type 'ITeam[] | undefined' is not assignable to ty... Remove this comment to see the full error message */}
            <TeamMembershipManagement teams={teams} canManageAgentTeams={canManageAgentTeams} />
            <AutoAssignmentManagement />
            <NotificationsManagement canAccessSettings={canAccessSettings} />
            {/* @ts-expect-error TS(2322): Type 'IDeviceListItem[] | undefined' is not assign... Remove this comment to see the full error message */}
            <BrowserNotificationsManagement agentId={agent.id} webpushSubs={agent.webpushSubscriptions} />
            <PermissionManagement agentRole={agent.permission} changePermissions={roleChangePermissions} />
            <AgentSignature
              canChange={canChangeSignature}
              agentID={agent.id}
              overflowRef={this.formBaseRef.current?.overflowRef ?? undefined}
            />
            {isSelfManagement && <Agent2FA agentId={agent.id} />}
            {isSelfManagement && (
              <AgentFormSection header={{ id: AgentFormSectionId.PasswordManagement, component: "Change password" }}>
                <Text>
                  To change the password, please log out and use the &quot;Forgot password?&quot; option on the login
                  screen.
                  <br />
                  <DeleteTextButton onClick={this.handleLogOutAgentButtonClick} type="button">
                    Log out
                  </DeleteTextButton>
                </Text>
              </AgentFormSection>
            )}
            {this.shouldShowDeletePanel() && (
              <AgentFormSection header={{ id: AgentFormSectionId.DeleteAgent, component: "Delete agent" }}>
                <Text>
                  You will delete the agent account and related statistics. Tickets will not be removed.
                  <br />
                  <DeleteTextButton
                    onClick={this.handleDeleteAgentButtonClick}
                    type="button"
                    data-testid="delete-agent"
                  >
                    Delete agent.
                  </DeleteTextButton>
                </Text>
              </AgentFormSection>
            )}
            {agentsLimitReachedModalShown && (
              <DSPortal zIndex={StackingContextLevel.ModalOverlay}>
                <SubscriptionUpgradeModal
                  paymentFailed={this.state.paymentFailed}
                  onModalClose={this.handleLimitReachedBack}
                  onSubmit={this.handleLimitReachedConfirm}
                />
              </DSPortal>
            )}
          </AgentFormBase>
          {agentDeleteModalShown && (
            <ModalPortal>
              <ConfirmActionModal
                heading={`Delete ${agent.name}`}
                onClose={this.handleAgentDeleteCancel}
                primaryAction={{
                  label: "Delete",
                  onClick: this.handleAgentDeleteConfirm,
                  actionType: "destructive",
                }}
                secondaryAction={{
                  label: "Cancel",
                  onClick: this.handleAgentDeleteCancel,
                }}
              >
                You will delete the agent account and related statistics. Tickets will not be removed.
              </ConfirmActionModal>
            </ModalPortal>
          )}
          {agentDeleteWarningModalShown && (
            <ModalPortal>
              <ConfirmActionModal
                heading="Can't delete this Agent"
                onClose={this.handleAgentDeleteWarningCancel}
                primaryAction={{
                  label: "Go to Settings",
                  onClick: onGoToMailboxes,
                  actionType: "primary",
                }}
                secondaryAction={{
                  label: "Cancel",
                  onClick: this.handleAgentDeleteWarningCancel,
                }}
              >
                There are forwarding addresses assigned to this Agent. Please detach forwarding addresses first.
              </ConfirmActionModal>
            </ModalPortal>
          )}
        </AttachmentProvider>
      </>
    );
  }
}
